import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StandardLayout from '../../../components/layout/StandardLayout';
import { XCircleIcon, TrashIcon } from '@heroicons/react/24/outline';

// Mock-Daten für Kategorien
const MOCK_CATEGORIES = [
    { id: 1, name: 'Navigation' },
    { id: 2, name: 'Wetterkunde' },
    { id: 3, name: 'Schifffahrtsrecht' },
    { id: 4, name: 'Seemannschaft I' },
    { id: 5, name: 'Seemannschaft II' }
];

const QuestionForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    // Refs für File-Inputs
    const questionImageRef = useRef(null);
    const answerImageRef = useRef(null);
    const explanationImageRef = useRef(null);

    const [formData, setFormData] = useState({
        question_text: '',
        question_image: '',
        answer_text: '',
        answer_image: '',
        explanation_text: '',
        explanation_image: '',
        category_id: ''
    });

    const [categories] = useState(MOCK_CATEGORIES);
    const [loading] = useState(false);
    const [error] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        // Mock-Daten für den Edit-Mode
        if (isEditMode) {
            setFormData({
                question_text: 'Beispielfrage für Edit-Mode',
                question_image: '',
                answer_text: 'Beispielantwort für Edit-Mode',
                answer_image: '',
                explanation_text: 'Beispielerklärung für Edit-Mode',
                explanation_image: '',
                category_id: '1'
            });
        }
    }, [id, isEditMode]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageUpload = async (e, fieldName) => {
        const file = e.target.files[0];
        if (file) {
            // Mock image upload - just create a local URL
            const url = URL.createObjectURL(file);
            setFormData(prev => ({
                ...prev,
                [fieldName]: url
            }));
        }
    };

    const handleImageDelete = (fieldName) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: ''
        }));

        // Reset des entsprechenden File-Input-Feldes
        switch(fieldName) {
            case 'question_image':
                if (questionImageRef.current) questionImageRef.current.value = '';
                break;
            case 'answer_image':
                if (answerImageRef.current) answerImageRef.current.value = '';
                break;
            case 'explanation_image':
                if (explanationImageRef.current) explanationImageRef.current.value = '';
                break;
            default:
                break;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Mock submit
        setSuccessMessage(isEditMode ? 'Frage erfolgreich aktualisiert!' : 'Frage erfolgreich erstellt!');
        
        if (!isEditMode) {
            setFormData({
                question_text: '',
                question_image: '',
                answer_text: '',
                answer_image: '',
                explanation_text: '',
                explanation_image: '',
                category_id: ''
            });
        }
        
        // Mock navigation delay
        setTimeout(() => {
            navigate('/admin/questions');
        }, 2000);
    };

    if (loading) {
        return (
            <StandardLayout>
                <div className="flex justify-center items-center min-h-screen">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </StandardLayout>
        );
    }

    return (
        <StandardLayout>
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-2xl font-bold mb-8">
                        {isEditMode ? 'Frage bearbeiten' : 'Neue Frage erstellen'}
                    </h1>

                    {error && (
                        <div className="alert alert-error mb-4">
                            <XCircleIcon className="w-6 h-6" />
                            <span>{error}</span>
                        </div>
                    )}

                    {successMessage && (
                        <div className="alert alert-success mb-4">
                            <span>{successMessage}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Kategorie Auswahl */}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Kategorie*</span>
                            </label>
                            <select 
                                name="category_id"
                                value={formData.category_id}
                                onChange={handleInputChange}
                                className="select select-bordered w-full"
                                required
                            >
                                <option value="">Kategorie auswählen</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Frage */}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Frage*</span>
                            </label>
                            <textarea
                                name="question_text"
                                value={formData.question_text}
                                onChange={handleInputChange}
                                className="textarea textarea-bordered h-24"
                                placeholder="Geben Sie hier die Frage ein"
                                required
                            />
                        </div>

                        {/* Frage Bild */}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Frage Bild</span>
                            </label>
                            <div className="space-y-2">
                                <input
                                    ref={questionImageRef}
                                    type="file"
                                    onChange={(e) => handleImageUpload(e, 'question_image')}
                                    className="file-input file-input-bordered w-full"
                                    accept="image/*"
                                />
                                {formData.question_image && (
                                    <div className="relative">
                                        <img 
                                            src={formData.question_image} 
                                            alt="Frage" 
                                            className="max-h-40 object-contain"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleImageDelete('question_image')}
                                            className="btn btn-circle btn-sm btn-error absolute top-2 right-2"
                                            title="Bild löschen"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Antwort */}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Antwort*</span>
                            </label>
                            <textarea
                                name="answer_text"
                                value={formData.answer_text}
                                onChange={handleInputChange}
                                className="textarea textarea-bordered h-24"
                                placeholder="Geben Sie hier die Antwort ein"
                                required
                            />
                        </div>

                        {/* Antwort Bild */}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Antwort Bild</span>
                            </label>
                            <div className="space-y-2">
                                <input
                                    ref={answerImageRef}
                                    type="file"
                                    onChange={(e) => handleImageUpload(e, 'answer_image')}
                                    className="file-input file-input-bordered w-full"
                                    accept="image/*"
                                />
                                {formData.answer_image && (
                                    <div className="relative">
                                        <img 
                                            src={formData.answer_image} 
                                            alt="Antwort" 
                                            className="max-h-40 object-contain"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleImageDelete('answer_image')}
                                            className="btn btn-circle btn-sm btn-error absolute top-2 right-2"
                                            title="Bild löschen"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Erklärung */}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Erklärung</span>
                            </label>
                            <textarea
                                name="explanation_text"
                                value={formData.explanation_text}
                                onChange={handleInputChange}
                                className="textarea textarea-bordered h-24"
                                placeholder="Geben Sie hier eine Erklärung ein (optional)"
                            />
                        </div>

                        {/* Erklärung Bild */}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Erklärung Bild</span>
                            </label>
                            <div className="space-y-2">
                                <input
                                    ref={explanationImageRef}
                                    type="file"
                                    onChange={(e) => handleImageUpload(e, 'explanation_image')}
                                    className="file-input file-input-bordered w-full"
                                    accept="image/*"
                                />
                                {formData.explanation_image && (
                                    <div className="relative">
                                        <img 
                                            src={formData.explanation_image} 
                                            alt="Erklärung" 
                                            className="max-h-40 object-contain"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleImageDelete('explanation_image')}
                                            className="btn btn-circle btn-sm btn-error absolute top-2 right-2"
                                            title="Bild löschen"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-4">
                            <button
                                type="button"
                                onClick={() => navigate('/admin')}
                                className="btn btn-ghost"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                            >
                                {isEditMode ? 'Aktualisieren' : 'Erstellen'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </StandardLayout>
    );
};

export default QuestionForm; 